import React, { useState } from "react";
import useDeviceType from "../../../custom-hooks/DeviceType";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import Button from "../../../component-lib/Molecules/button/Button";
import CloseIcon from "@mui/icons-material/Close";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import { STATUS_FILTER_FOR_TASK } from "../constant";
import EventEHRLazyDropdown from "../../../component-lib/Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import {
  hideLoader,
  showLoader,
} from "../../../component-lib/Molecules/loader/LoaderSlice";
import { useDispatch, useSelector } from "react-redux";
import { getLazyLoadingApiResponse, getLazyLoadingSearchApiResponse } from "../../../utils/constantsFunctions";
import { getTaskTypeList } from "../taskSaga";
import { componentKey } from "../taskSlice";
import SettingsService from "../../../services/SettingsService";

interface FilterProps {
  onApply: (values: any) => void;
  onClear: () => void;
  defaultSelectedData?: Record<string, any>;
  handleClose: () => void;
  assignToDropdownProps: {
    label: string;
    dropdownOptions: any;
    dropdownSearch: string;
    selectedValue: any;
    setDropdownSearch: (data: any) => void;
    setSelectedValue: (data: any) => void;
    setPageCount: (data: any) => void;
    setResetFlag: (data: boolean) => void;
  };
  // Pre-selected "Assign To" (e.g. current user when opened from a notification).
  prefilledAssignTo?: { label: string; value: string } | null;
  children?: React.ReactNode;
}

interface TaskFilterDataProps {
  assignTo: string;
  taskType: string;
  date: any;
  status: string;
  taskCategory: string;
  excludeTaskCategory: string;
}

const TaskFilter = ({
  onApply,
  onClear,
  handleClose,
  prefilledAssignTo,
}: FilterProps) => {
  const deviceType = useDeviceType();
  const dispatch = useDispatch();

  const { taskTypeListData } = useSelector((state: any) => state[componentKey]);

  // TASK TYPE OPTIONS from master data (type = task_type); name is label & value.
  const taskTypeOptions = (taskTypeListData ?? [])
    .filter((item: any) => item?.name)
    .map((item: any) => ({ label: item.name, value: item.name }));

  React.useEffect(() => {
    dispatch(getTaskTypeList());
  }, []);

  const [pageCount, setPageCount] = React.useState(1);
  const [dropdownSearch, setDropdownSearch] = React.useState("");

  const [dropdownOptions, setDropdownOptions] = React.useState<any>([]);

  const [selectedAssignTo, setSelectedAssignTo] = useState<any>(
    prefilledAssignTo ?? null
  );

  const [resetAssignTo, setResetAssignTo] = React.useState(false);

  const [resetFlag, setResetFlag] = React.useState(false);
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);

  // Simple flag to prevent multiple initial API calls
  const initialLoadComplete = React.useRef(false);

  const [taskFilterData, setTaskFilterData] = useState<TaskFilterDataProps>({
    assignTo: prefilledAssignTo?.value ?? "",
    taskType: "",
    date: undefined,
    // Mirrors the list's default so the dropdown matches what's applied.
    status: "NotCompleted",
    taskCategory: "",
    excludeTaskCategory: "",
  });

  // Category = the part of a task-type name before " - " (whole name if no
  // dash). Derived from the already-loaded task types so it self-maintains when
  // categories are added/removed. Shared by the Category and Exclude Category
  // dropdowns. "All Categories" (value "") clears the selection inline.
  const taskCategoryOptions = [
    { label: "All Categories", value: "" },
    ...Array.from(
      new Set(
        (taskTypeListData ?? [])
          .filter((i: any) => i?.name)
          .map((i: any) => i.name.split(" - ")[0].trim()),
      ),
    )
      .sort()
      .map((c: any) => ({ label: c, value: c })),
  ];

  // Task Type server-search (searchValue) over master data; falls back to the
  // full browse list (taskTypeOptions) when the search box is empty.
  const [taskTypeSearch, setTaskTypeSearch] = React.useState("");
  const [taskTypeSearchResults, setTaskTypeSearchResults] =
    React.useState<any>(null);
  const [taskTypeTimer, setTaskTypeTimer] =
    React.useState<NodeJS.Timeout | null>(null);

  const searchTaskTypes = async (search: string) => {
    try {
      const res = await SettingsService.getMasterDataList({
        type: "task_type",
        searchValue: search,
      });
      const rows = res?.data?.masterData ?? [];
      setTaskTypeSearchResults(
        rows
          .filter((r: any) => r?.name)
          .map((r: any) => ({ label: r.name, value: r.name }))
      );
    } catch {
      setTaskTypeSearchResults([]);
    }
  };

  const handleTaskTypeSearch = (val: string) => {
    if (taskTypeTimer) clearTimeout(taskTypeTimer);
    setTaskTypeSearch(val);
    if (!val) {
      setTaskTypeSearchResults(null);
      return;
    }
    const t = setTimeout(() => searchTaskTypes(val), 500);
    setTaskTypeTimer(t);
  };

  const shownTaskTypeOptions =
    taskTypeSearch && taskTypeSearchResults
      ? taskTypeSearchResults
      : taskTypeOptions;
  const selectedTaskType = taskFilterData?.taskType
    ? { label: taskFilterData.taskType, value: taskFilterData.taskType }
    : null;

  const isFormFilled = Object.values(taskFilterData).some(
    (value) => value !== "" && value !== undefined
  );

  const adjustedGrid =
    deviceType === "tablet" || deviceType === "small-tablet"
      ? "1fr 1fr"
      : deviceType === "mobile"
        ? "1fr"
        : "1fr 1fr 1fr 1fr";

  const headerStyle = {
    fontWeight: "500",
    fontSize: "16px",
    paddingBottom: "10px",
  };

  const commonGridStyle = {
    gridTemplateColumns: adjustedGrid,
    padding: "0px 0px 12px 0px",
    display: "grid",
    gap: "24px 21px",
  };

  const resetList = () => {
    setSelectedAssignTo(null);
    setDropdownSearch("");
    setPageCount(1);
    getDropdownList(1, true);
  };

  // Load initial data only once
  React.useEffect(() => {
    if (!initialLoadComplete.current) {
      getDropdownList(1, true);
      initialLoadComplete.current = true;
    }
  }, []); // Run only once on mount

  React.useEffect(() => {
    if (initialLoadComplete.current && !resetAssignTo) {
      if (dropdownSearch && dropdownSearch.length > 0) {
        getDropdownSearchList(dropdownSearch);
      } else if (pageCount > 1) {
        getDropdownList(pageCount);
      }
    }
  }, [dropdownSearch, pageCount, resetAssignTo]);

  React.useEffect(() => {
    if (resetAssignTo) {
      resetList();
      setResetAssignTo(false);
    }
  }, [resetAssignTo]);

  // Debounced Assign To search input.
  const handleSearchedValueChange = (data: any) => {
    if (timer) {
      clearTimeout(timer);
    }
    const newTimer = setTimeout(() => {
      setDropdownSearch(data);
    }, 500);
    setTimer(newTimer);
  };

  // GET DROPDOWN LIST (Assign To users — paged, appends on scroll-to-bottom)
  const getDropdownList = async (pageCount: any, reset = false) => {
    dispatch(showLoader());
    try {
      const response = await getLazyLoadingApiResponse("user", pageCount, 10, {
        isTaskAssignment: true,
      });
      const usersList = response?.data?.users || [];
      const mappedUserData = usersList.map((user: any) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: `${user.id}`,
      }));

      if (mappedUserData && mappedUserData?.length > 0) {
        if (reset) {
          setDropdownOptions(mappedUserData);
          setResetFlag(false);
        } else {
          setDropdownOptions((prev: any) => prev.concat(mappedUserData));
        }
      }
    } catch (error) {
      console.error("Error fetching dropdown list:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // GET DROPDOWN SEARCH LIST (Assign To users — server search)
  const getDropdownSearchList = async (search: string) => {
    dispatch(showLoader());
    try {
      const response = await getLazyLoadingSearchApiResponse(
        "user",
        search,
        "name",
        { isTaskAssignment: true }
      );
      const usersList = response?.data?.users || [];
      const mappedUserData = usersList.map((user: any) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: `${user.id}`,
      }));
      if (mappedUserData?.length) {
        setDropdownOptions(mappedUserData);
      }
    } catch (error) {
      console.error("Error fetching search dropdown list:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <div
      style={{ border: "1px solid #D2D2D2" }}
      className="py-4 px-3 rounded mb-6"
    >
      <div className="w-full flex flex-col gap-6 rounded-lg">
        {/* PATIENT DETAILS */}
        <div>
          <div className="flex justify-between mb-2">
            <Heading title="Filter" customStyle={headerStyle} />
            <div onClick={handleClose} className="cursor-pointer">
              {" "}
              <CloseIcon />
            </div>
          </div>
          <div style={commonGridStyle}>
            {/* Task Type Dropdown (master data, server-searchable) */}
            <EventEHRLazyDropdown
              label="Task Type"
              dropdownIcon={true}
              dropdownOptions={shownTaskTypeOptions}
              dropdownSearch={taskTypeSearch}
              selectedValue={selectedTaskType}
              filterOptions={(o: any) => o}
              setDropdownSearch={(data: any) => handleTaskTypeSearch(data)}
              setPageCount={() => {}}
              setResetFlag={() => {
                setTaskTypeSearch("");
                setTaskTypeSearchResults(null);
              }}
              setSelectedValue={(data: any) => {
                setTaskFilterData((prevData) => ({
                  ...prevData,
                  taskType: data?.value ?? "",
                }));
              }}
            />

            {/* Assign To Auto Select */}
            <EventEHRLazyDropdown
              label="Assign To"
              dropdownIcon={true}
              dropdownOptions={dropdownOptions}
              dropdownSearch={dropdownSearch}
              selectedValue={selectedAssignTo}
              setDropdownSearch={(data) => handleSearchedValueChange(data)}
              setPageCount={(data) => {
                setPageCount(data);
              }}
              setResetFlag={(data) => {
                setResetFlag(data);
              }}
              // Remove the `X` button functionality by preventing individual clearing of selection
              setSelectedValue={(data) => {
                setSelectedAssignTo(data ?? null);

                setTaskFilterData((prevData) => ({
                  ...prevData,
                  assignTo: data?.value ?? "",
                }));
              }}
            />

            <DatePicker
              name={taskFilterData?.date}
              value={taskFilterData?.date}
              onChangeCb={(value) => {
                setTaskFilterData((prevData) => ({
                  ...prevData,
                  date: value,
                }));
              }}
              label="Due Date"
              style={{ width: "100%" }}
              // maxDate={moment()}
            />

            <SelectDropdown
              label="Status"
              name={"status"}
              value={taskFilterData?.status}
              options={STATUS_FILTER_FOR_TASK}
              onChangeCb={(e) => {
                setTaskFilterData((prevData) => ({
                  ...prevData,
                  status: e.target.value,
                }));
              }}
            />

            {/* Category — the task-type group (part before " - ") */}
            <SelectDropdown
              label="Category"
              name={"taskCategory"}
              value={taskFilterData?.taskCategory}
              options={taskCategoryOptions}
              onChangeCb={(e) => {
                setTaskFilterData((prevData) => ({
                  ...prevData,
                  taskCategory: e.target.value,
                }));
              }}
            />

            {/* Exclude Category — hide a whole category (e.g. JamboCare EHR) */}
            <SelectDropdown
              label="Exclude Category"
              name={"excludeTaskCategory"}
              value={taskFilterData?.excludeTaskCategory}
              options={taskCategoryOptions}
              onChangeCb={(e) => {
                setTaskFilterData((prevData) => ({
                  ...prevData,
                  excludeTaskCategory: e.target.value,
                }));
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <Button
          label={TEXT_CONSTANTS?.CLEAR_ALL}
          variant="outlined"
          onClickCb={() => {
            setTaskFilterData({
              assignTo: "",
              taskType: "",
              date: undefined,
              status: "",
              taskCategory: "",
              excludeTaskCategory: "",
            });
            setSelectedAssignTo(null);
            onClear && onClear();
          }}
          style={{
            width: "120px",
            height: "44px",
            padding: "12px 20px",
            fontSize: "16px",
            backgroundColor: "white",
            border: "none",
            color: "#005596",
          }}
          //variant={undefined}        />
        />
        <Button
          type="submit"
          label={TEXT_CONSTANTS?.APPLY}
          variant={isFormFilled ? "outlined" : "contained"}
          disabled={!isFormFilled}
          onClickCb={() => {
            onApply(taskFilterData);
          }}
          style={{
            background: isFormFilled ? "#E4F3FF" : "",
            color: isFormFilled ? "#005596" : "",
            width: "120px",
            height: "44px",
            padding: "12px 20px",
            fontSize: "16px",
          }}
        ></Button>
      </div>
    </div>
  );
};
export default TaskFilter;
